"use client";
import { parseISO, format, startOfMonth, endOfMonth } from "date-fns";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  ComposedChart,
  ReferenceArea,
  ResponsiveContainer,
  Bar,
  ReferenceLine,
} from "recharts";
import { Button } from "../chartUi/button";
import { Card, CardContent, CardHeader, CardTitle } from "../chartUi/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "../chartUi/chart";

import { DictionaryProps } from "@/types";

import * as S from "./styles";

type DataPoint = Record<string, any>;

type ZoomableChartProps = {
  data?: DataPoint[];
  titleChart: string;
  xAxisDataKey: string;
  yAxisDataKey: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  tooltipNameKey?: string;
  dict?: DictionaryProps["dict"];
  chartConfig: ChartConfig;
  heightFull?: boolean;
};

const seedRandom = (seed: number) => {
  const x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
};

export function simulateData(
  start = "2024-01-01T00:00:00Z",
  end = "2024-01-02T00:00:00Z",
): DataPoint[] {
  const simulatedData = [];
  let baseValue = 50;
  for (
    let currentDate = new Date(start);
    currentDate <= new Date(end);
    currentDate.setTime(currentDate.getTime() + 600000)
  ) {
    const seed = currentDate.getTime();
    baseValue = Math.max(
      (baseValue +
        ((0.5 * (currentDate.getTime() - new Date(start).getTime())) /
          (new Date(end).getTime() - new Date(start).getTime())) *
          100 +
        (seedRandom(seed) - 0.5) * 20 +
        (seedRandom(seed + 1) < 0.1 ? (seedRandom(seed + 2) - 0.5) * 50 : 0) +
        Math.sin(currentDate.getTime() / 3600000) * 10) *
        (1 + (seedRandom(seed + 3) - 0.5) * 0.2),
      1,
    );
    simulatedData.push({
      date: currentDate.toISOString(),
      events: Math.max(Math.floor(baseValue), 1),
    });
  }
  return simulatedData;
}

export function ZoomableChartArea({
  data: initialData,
  titleChart,
  xAxisDataKey,
  yAxisDataKey,
  xAxisLabel,
  yAxisLabel,
  tooltipNameKey,
  dict,
  chartConfig,
  heightFull = false,
}: ZoomableChartProps) {
  const [data, setData] = useState<DataPoint[]>(initialData || []);
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [endTime, setEndTime] = useState<number | null>(null);
  const [hoveredX, setHoveredX] = useState<string | number | null>(null);
  const [hoveredY, setHoveredY] = useState<number | null>(null);
  const [originalData, setOriginalData] = useState<DataPoint[]>(
    initialData || [],
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const color = chartConfig[yAxisDataKey]?.color || "#0052FF";

  useEffect(() => {
    if (initialData?.length) {
      // Normaliza datas usando date-fns
      const parseToTimestamp = (val: any) => {
        if (!val) return null;
        if (typeof val === "number") return val;
        if (typeof val === "string") {
          // Tenta parse ISO ou yyyy-MM-dd
          try {
            return parseISO(val).getTime();
          } catch {
            return null;
          }
        }
        return null;
      };

      const normalized = initialData.map((d) => ({
        ...d,
        [xAxisDataKey]: parseToTimestamp(d[xAxisDataKey]),
      }));

      setData(normalized);
      setOriginalData(normalized);

      // Usa o primeiro item para definir o mês
      const firstTs = normalized[0]?.[xAxisDataKey];
      if (firstTs) {
        const monthStart = startOfMonth(new Date(firstTs)).getTime();
        const monthEnd = endOfMonth(new Date(firstTs)).getTime();
        setStartTime(monthStart);
        setEndTime(monthEnd);
      } else {
        setStartTime(normalized[0]?.[xAxisDataKey] ?? null);
        setEndTime(normalized[normalized.length - 1]?.[xAxisDataKey] ?? null);
      }
    }
  }, [initialData, xAxisDataKey]);

  const zoomedData = useMemo(() => {
    if (startTime === null || endTime === null || !xAxisDataKey) return data;

    // If start/end are numeric timestamps, filter by numeric range
    if (typeof startTime === "number" && typeof endTime === "number") {
      const start = startTime;
      const end = endTime;
      const filtered = originalData.filter((dataPoint) => {
        const curr = Number(dataPoint[xAxisDataKey]);
        return typeof curr === "number" && curr >= start && curr <= end;
      });
      return filtered.length > 1 ? filtered : originalData.slice(0, 2);
    }

    // Fallback: categorical filtering
    const startIdx = originalData.findIndex(
      (d) => d[xAxisDataKey] === startTime,
    );
    const endIdx = originalData.findIndex((d) => d[xAxisDataKey] === endTime);
    if (startIdx !== -1 && endIdx !== -1) {
      const [from, to] = [startIdx, endIdx].sort((a, b) => a - b);
      const filtered = originalData.slice(from, to + 1);
      return filtered.length > 1 ? filtered : originalData.slice(0, 2);
    }

    return originalData;
  }, [startTime, endTime, originalData, data, xAxisDataKey]);

  const total = useMemo(
    // Calcular o total usando a chave dinâmica do eixo Y
    () =>
      zoomedData.reduce(
        (acc, curr) => acc + (Number(curr[yAxisDataKey]) || 0),
        0,
      ),
    [zoomedData, yAxisDataKey],
  );

  const handleMouseDown = (e: any) => {
    if (e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
      setIsSelecting(true);
    }
  };

  const handleMouseMove = (e: any) => {
    if (isSelecting && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
      return;
    }

    // Update hover crosshair when not selecting
    if (!isSelecting && e && e.activeLabel) {
      setHoveredX(e.activeLabel);
      const payloadItem = e.activePayload && e.activePayload[0];
      const yVal = payloadItem
        ? (payloadItem.value ?? payloadItem.payload?.[yAxisDataKey])
        : null;
      setHoveredY(typeof yVal === "number" ? yVal : null);
    }
  };

  const handleMouseUp = () => {
    if (refAreaLeft && refAreaRight) {
      let left: any = refAreaLeft;
      let right: any = refAreaRight;
      if (typeof left === "number" && typeof right === "number") {
        [left, right] = [left, right].sort((a: number, b: number) => a - b);
      } else {
        [left, right] = [String(left), String(right)].sort();
      }
      setStartTime(left as number);
      setEndTime(right as number);
    }
    setRefAreaLeft(null);
    setRefAreaRight(null);
    setIsSelecting(false);
    setHoveredX(null);
    setHoveredY(null);
  };

  const handleReset = () => {
    if (originalData.length > 0) {
      setStartTime(originalData[0]?.[xAxisDataKey]);
      setEndTime(originalData[originalData.length - 1]?.[xAxisDataKey]);
    }
  };

  const isDateAxis = (value: any) => {
    const date = new Date(value);
    return !isNaN(date.getTime());
  };

  const handleZoom = (
    e: React.WheelEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    //e.preventDefault();

    if (
      !originalData.length ||
      !chartRef.current ||
      typeof startTime !== "number" ||
      typeof endTime !== "number"
    ) {
      console.warn(
        "Zooming requires date-like xAxisDataKey and valid start/end times.",
      );
      return;
    }

    // Só permite zoom se o eixo X for data
    if (!isDateAxis(startTime) || !isDateAxis(endTime)) {
      // Para categorias, desabilita zoom
      return;
    }

    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.warn("StartTime or EndTime is not a valid date. Zoom disabled.");
      return;
    }

    const zoomFactor = 0.1;
    let direction = 0;
    let clientX = 0;

    if ("deltaY" in e) {
      // Evento roda do mouse
      direction = e.deltaY < 0 ? 1 : -1;
      clientX = e.clientX;
    } else if ("touches" in e && e.touches.length === 2) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY,
      );

      if ((e as any).lastTouchDistance) {
        direction = currentDistance > (e as any).lastTouchDistance ? 1 : -1;
      }
      (e as any).lastTouchDistance = currentDistance;

      clientX = (touch1.clientX + touch2.clientX) / 2;
    } else {
      // Evento não suportado para zoom
      return;
    }

    const currentRange = endDate.getTime() - startDate.getTime();
    const zoomAmount = currentRange * zoomFactor * direction;

    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = clientX - chartRect.left;
    const chartWidth = chartRect.width;
    const mousePercentage = mouseX / chartWidth;

    const newStartTime = new Date(
      startDate.getTime() + zoomAmount * mousePercentage,
    );
    const newEndTime = new Date(
      endDate.getTime() - zoomAmount * (1 - mousePercentage),
    );

    // Evita intervalo invertido ou muito pequeno
    if (newEndTime <= newStartTime) {
      return;
    }

    setStartTime(newStartTime.getTime());
    setEndTime(newEndTime.getTime());
  };

  // Adiciona suporte a zoom com a roda do mouse sem afetar o scroll da página
  useEffect(() => {
    const chartDiv = chartRef.current;
    if (!chartDiv) return;

    const wheelHandler = (e: WheelEvent) => {
      // Não bloqueia o scroll da página
      handleZoom(e as any);
    };

    chartDiv.addEventListener("wheel", wheelHandler, { passive: true });

    return () => {
      chartDiv.removeEventListener("wheel", wheelHandler);
    };
  }, [handleZoom]);

  const formatXAxis = (tickItem: any) => {
    if (typeof tickItem === "number") {
      return format(new Date(tickItem), "dd/MMM", { locale: undefined });
    }
    if (typeof tickItem === "string") {
      try {
        return format(parseISO(tickItem), "dd/MMM", { locale: undefined });
      } catch {
        return tickItem;
      }
    }
    return String(tickItem);
  };

  return (
    <S.CardChart className="w-full h-full">
      <S.ChartHeader className="flex items-stretch space-y-0 border-b p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1">
          <CardTitle>{titleChart}</CardTitle>
        </div>
        <div className="flex">
          <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 text-left">
            <span className="text-xs text-muted-foreground">{dict?.Total}</span>
            <span className="text-lg font-bold leading-none sm:text-3xl">
              {total.toLocaleString()}
            </span>
          </div>
        </div>
      </S.ChartHeader>
      <S.ChartContainer className="px-2 sm:p-6 h-full sm:h-[calc(100%-150px)]">
        <ChartContainer config={chartConfig} className="w-full h-full">
          <div
            className="h-full"
            onTouchMove={handleZoom}
            ref={chartRef}
            style={{ touchAction: "none" }}
          >
            <div className="flex justify-end my-2 sm:mb-4">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!startTime && !endTime}
                className="text-xs sm:text-sm"
              >
                Reset
              </Button>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={zoomedData}
                margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 0,
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <defs>
                  <linearGradient id="colorMain" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#fff" stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  vertical={true}
                  horizontal={true}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey={xAxisDataKey}
                  type="number"
                  scale="time"
                  domain={[
                    startTime !== null ? startTime : "dataMin",
                    endTime !== null ? endTime : "dataMax",
                  ]}
                  tickFormatter={formatXAxis}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  minTickGap={16}
                  style={{
                    fontSize: "10px",
                    userSelect: "none",
                  }}
                  // label={{
                  //     value: xAxisLabel,
                  //     position: "insideBottomCenter",
                  //     dy: 25,
                  //     style: { textAnchor: "middle", fontWeight: "bold" },
                  // }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  style={{
                    fontSize: "10px",
                    userSelect: "none",
                  }}
                  width={30}
                  label={{
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 12,
                  }}
                />
                <ChartTooltip
                  cursor={{
                    stroke: "var(--border)",
                    strokeWidth: 1,
                    strokeDasharray: "3 3",
                  }}
                  content={
                    <ChartTooltipContent
                      className="w-[150px] sm:w-[200px] font-mono text-xs sm:text-sm"
                      nameKey={tooltipNameKey}
                      labelFormatter={(value) => {
                        // If it's a timestamp number, format as date
                        if (typeof value === "number") {
                          const d = new Date(value);
                          if (!isNaN(d.getTime())) return d.toLocaleString();
                        }
                        // If it's a string like YYYY-MM-DD or ISO, try parse
                        if (
                          typeof value === "string" &&
                          (value.includes("T") ||
                            value.includes("-") ||
                            /^\d{4}-\d{2}-\d{2}$/.test(value))
                        ) {
                          const date = new Date(value);
                          if (!isNaN(date.getTime())) {
                            return date.toLocaleString();
                          }
                        }
                        return String(value);
                      }}
                      formatter={(value: any, name: any) => [
                        `${tooltipNameKey || name}:  `,
                        value,
                      ]}
                    />
                  }
                />
                <ChartLegend content={<ChartLegendContent />} />
                <Area
                  type="monotone"
                  dataKey={yAxisDataKey}
                  stroke={color}
                  fillOpacity={1}
                  fill="url(#colorMain)"
                  isAnimationActive={false}
                />
                {hoveredX && (
                  <ReferenceLine
                    x={hoveredX}
                    stroke="var(--border)"
                    strokeDasharray="3 3"
                  />
                )}
                {hoveredY !== null && (
                  <ReferenceLine
                    y={hoveredY}
                    stroke="var(--border)"
                    strokeDasharray="3 3"
                  />
                )}
                {refAreaLeft && refAreaRight && (
                  <ReferenceArea
                    x1={refAreaLeft}
                    x2={refAreaRight}
                    strokeOpacity={0.3}
                    fill="hsl(var(--foreground))"
                    fillOpacity={0.05}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </ChartContainer>
      </S.ChartContainer>
    </S.CardChart>
  );
}
